import { useEffect, useState } from "react";
import { Icon, EmptyState } from "../../components/Common.jsx";
import { useApp } from "../../store.jsx";
import { listPlans, updatePlan } from "../../lib/adminApi";
import { formatMoney, paiseToRupees, rupeesToPaise } from "../../lib/money.js";
import TableSkeleton from "../../components/admin/TableSkeleton.jsx";
import AdminPageBoundary from "../../components/admin/AdminPageBoundary.jsx";

const FEATURE_FLAGS = [
  ["departments", "Departments"],
  ["customTemplates", "Custom templates"],
  ["slackIntegration", "Slack integration"],
  ["apiAccess", "API access"],
  ["analyticsAdvanced", "Advanced analytics"],
  ["customReminders", "Custom reminders"],
  ["sso", "SSO"],
];

function PlansInner() {
  const { toast, openModal, closeModal } = useApp();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setPlans(await listPlans());
    } catch (e) {
      toast(e.message || "Failed to load plans", "error");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    const draft = {
      name: p.name,
      amountRupees: paiseToRupees(p.amount),
      isActive: p.isActive,
      features: { ...(p.features || {}) },
    };
    // Seed complete rates/min objects (paise) so editing one field never
    // drops the others, and inputs always have a resolved default value.
    if (p.key === "custom") {
      draft.features.rates = {
        perEmployee: p.features?.rates?.perEmployee ?? 1200,
        perManager: p.features?.rates?.perManager ?? 7800,
        perReport: p.features?.rates?.perReport ?? 25,
      };
      draft.features.min = {
        employees: p.features?.min?.employees ?? 5,
        managers: p.features?.min?.managers ?? 5,
        reports: p.features?.min?.reports ?? 200,
      };
    }
    const setFeat = (k, v) => { draft.features = { ...draft.features, [k]: v }; };
    // Custom-plan per-unit rates are stored in paise; the inputs take rupees.
    const setRate = (k, rupees) => {
      draft.features = {
        ...draft.features,
        rates: { ...(draft.features.rates || {}), [k]: rupeesToPaise(rupees) },
      };
    };
    const setMin = (k, v) => {
      draft.features = {
        ...draft.features,
        min: { ...(draft.features.min || {}), [k]: Math.max(1, Math.round(Number(v) || 0)) },
      };
    };
    const save = async () => {
      const amountPaise = rupeesToPaise(draft.amountRupees);
      if (amountPaise < 0) return toast("Amount must be ≥ 0", "error");
      setSaving(true);
      try {
        await updatePlan(p.key, {
          name: draft.name,
          amount: amountPaise,
          isActive: draft.isActive,
          features: draft.features,
        });
        toast("Plan updated", "success");
        closeModal();
        load();
      } catch (e) {
        toast(e.message, "error");
      } finally {
        setSaving(false);
      }
    };
    openModal(
      <>
        <div className="modal-head">
          <h3>Edit {p.name}</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="field">
              <label>Name</label>
              <input
                className="input"
                defaultValue={draft.name}
                onChange={(e) => (draft.name = e.target.value)}
              />
            </div>
            <div className="field">
              <label>Price (₹ per month)</label>
              <input
                className="input"
                type="number"
                min={0}
                step="0.01"
                defaultValue={draft.amountRupees}
                onChange={(e) => (draft.amountRupees = Number(e.target.value))}
              />
              <small style={{ fontSize: 11, color: "var(--text-muted)" }}>
                Saved as paise. {formatMoney(rupeesToPaise(draft.amountRupees))}
              </small>
            </div>
          </div>
          {p.key === "custom" && (
            <div
              className="field"
              style={{
                marginTop: 4,
                padding: 14,
                border: "1px solid var(--border)",
                borderRadius: 10,
                background: "var(--surface-2)",
              }}
            >
              <label style={{ fontWeight: 600 }}>
                Custom plan pricing (₹ per unit / month)
              </label>
              <div className="form-row" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))" }}>
                <div className="field">
                  <label style={{ fontSize: 12, color: "var(--text-muted)" }}>Per employee</label>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step="0.01"
                    defaultValue={paiseToRupees(draft.features.rates?.perEmployee ?? 1200)}
                    onChange={(e) => setRate("perEmployee", e.target.value)}
                  />
                </div>
                <div className="field">
                  <label style={{ fontSize: 12, color: "var(--text-muted)" }}>Per manager</label>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step="0.01"
                    defaultValue={paiseToRupees(draft.features.rates?.perManager ?? 7800)}
                    onChange={(e) => setRate("perManager", e.target.value)}
                  />
                </div>
                <div className="field">
                  <label style={{ fontSize: 12, color: "var(--text-muted)" }}>Per report</label>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    step="0.01"
                    defaultValue={paiseToRupees(draft.features.rates?.perReport ?? 25)}
                    onChange={(e) => setRate("perReport", e.target.value)}
                  />
                </div>
              </div>
              <label style={{ marginTop: 10, display: "block" }}>Minimum units an owner can pick</label>
              <div className="form-row" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))" }}>
                <div className="field">
                  <label style={{ fontSize: 12, color: "var(--text-muted)" }}>Min employees</label>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    defaultValue={draft.features.min?.employees ?? 5}
                    onChange={(e) => setMin("employees", e.target.value)}
                  />
                </div>
                <div className="field">
                  <label style={{ fontSize: 12, color: "var(--text-muted)" }}>Min managers</label>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    defaultValue={draft.features.min?.managers ?? 5}
                    onChange={(e) => setMin("managers", e.target.value)}
                  />
                </div>
                <div className="field">
                  <label style={{ fontSize: 12, color: "var(--text-muted)" }}>Min reports</label>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    defaultValue={draft.features.min?.reports ?? 200}
                    onChange={(e) => setMin("reports", e.target.value)}
                  />
                </div>
              </div>
              <small style={{ fontSize: 11, color: "var(--text-muted)", display: "block", marginTop: 6 }}>
                Owner price = (employees × per-employee) + (managers × per-manager) + (reports × per-report).
                Tip: ₹0.25/report → enter 0.25.
              </small>
            </div>
          )}
          {p.key === "free_trial" && (
            <div className="form-row">
              <div className="field">
                <label>Trial days</label>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={90}
                  defaultValue={Number(draft.features.trialDays) || 14}
                  onChange={(e) => setFeat("trialDays", Number(e.target.value))}
                />
              </div>
            </div>
          )}
          <div className="form-row">
            <div className="field">
              <label>Max employees (-1 = ∞)</label>
              <input
                className="input"
                type="number"
                defaultValue={draft.features.maxEmployees}
                onChange={(e) => setFeat("maxEmployees", Number(e.target.value))}
              />
            </div>
            <div className="field">
              <label>Max managers</label>
              <input
                className="input"
                type="number"
                defaultValue={draft.features.maxManagers}
                onChange={(e) => setFeat("maxManagers", Number(e.target.value))}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="field">
              <label>Reports / month</label>
              <input
                className="input"
                type="number"
                defaultValue={draft.features.reportsPerMonth}
                onChange={(e) => setFeat("reportsPerMonth", Number(e.target.value))}
              />
            </div>
            <div className="field">
              <label>WhatsApp numbers</label>
              <input
                className="input"
                type="number"
                defaultValue={draft.features.whatsappNumbers}
                onChange={(e) => setFeat("whatsappNumbers", Number(e.target.value))}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="field">
              <label>AI Analysis</label>
              <select className="input" defaultValue={draft.features.aiAnalysis}
                onChange={(e) => setFeat("aiAnalysis", e.target.value)}>
                <option value="basic">basic</option>
                <option value="advanced">advanced</option>
              </select>
            </div>
            <div className="field">
              <label>Active</label>
              <select className="input" defaultValue={draft.isActive ? "1" : "0"}
                onChange={(e) => (draft.isActive = e.target.value === "1")}>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
          </div>
          <div className="field">
            <label>Feature flags</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8 }}>
              {FEATURE_FLAGS.map(([k, label]) => (
                <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    defaultChecked={!!draft.features[k]}
                    onChange={(e) => setFeat(k, e.target.checked)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div style={{ fontSize: 12, color: "var(--warning)", marginTop: 8 }}>
            Changes apply to subscriptions created AFTER save. Existing
            subscriptions retain their stored amount until renewal.
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            <Icon name="check" size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </>,
      "lg",
    );
  };

  const sortedPlans = [...plans].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const featuredKey = sortedPlans[Math.min(1, sortedPlans.length - 1)]?.key;

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Plan Catalog</h1>
          <p className="page-sub">Edit price & limits for all subscription plans</p>
        </div>
      </div>

      {loading ? (
        <TableSkeleton columns={4} rows={3} />
      ) : plans.length === 0 ? (
        <EmptyState title="No plans" desc="Seed plans with `npm run seed:plans`." icon="tag" />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
          {sortedPlans.map((p) => {
            const isFeatured = p.key === featuredKey;
            const trialDays = Number(p.features?.trialDays) || 0;
            return (
              <div
                key={p.key}
                className="card"
                style={{
                  padding: 22,
                  borderRadius: 16,
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  background: isFeatured
                    ? "linear-gradient(135deg, rgba(139,92,246,0.08), rgba(99,102,241,0.04))"
                    : "var(--surface)",
                  borderColor: isFeatured ? "rgba(139,92,246,0.35)" : "var(--border)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1.2, color: "var(--text-muted)", fontWeight: 600 }}>
                      {p.key.replace(/_/g, " ")}
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>
                      {p.name}
                    </div>
                  </div>
                  {p.isActive
                    ? <span className="badge badge-success"><span className="badge-dot" />Active</span>
                    : <span className="badge badge-muted"><span className="badge-dot" />Inactive</span>}
                </div>

                <div style={{ marginTop: 14, fontSize: 32, fontWeight: 800 }}>
                  {p.key === "custom"
                    ? "Custom"
                    : p.amount === 0 && p.key === "free_trial"
                      ? "Free"
                      : formatMoney(p.amount, p.currency || "INR")}
                  {p.amount > 0 && (
                    <span style={{ fontSize: 14, color: "var(--text-muted)", fontWeight: 500 }}> /mo</span>
                  )}
                </div>

                <div style={{ marginTop: 16, marginBottom: 18, display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
                  {trialDays > 0 && <Feature label="Trial days" value={trialDays} />}
                  <Feature label="Employees" value={p.features?.maxEmployees === -1 ? "Unlimited" : p.features?.maxEmployees} />
                  <Feature label="Managers" value={p.features?.maxManagers === -1 ? "Unlimited" : p.features?.maxManagers} />
                  <Feature label="Reports / mo" value={p.features?.reportsPerMonth === -1 ? "Unlimited" : p.features?.reportsPerMonth} />
                  <Feature label="WhatsApp #" value={p.features?.whatsappNumbers === -1 ? "Unlimited" : p.features?.whatsappNumbers} />
                  <Feature label="AI Analysis" value={p.features?.aiAnalysis} />
                </div>

                <button
                  className="btn btn-primary"
                  style={{ width: "100%", marginTop: "auto", justifyContent: "center" }}
                  onClick={() => openEdit(p)}
                >
                  <Icon name="edit-2" size={14} /> Edit Plan
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function Feature({ label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Icon name="check" size={14} style={{ color: "var(--success)" }} />
      <span style={{ color: "var(--text-muted)" }}>{label}:</span>
      <strong style={{ marginLeft: "auto" }}>{value ?? "—"}</strong>
    </div>
  );
}

export default function AdminPlans() {
  return (
    <AdminPageBoundary>
      <PlansInner />
    </AdminPageBoundary>
  );
}
